import { SettingsCard } from "../../../../layout/portalPageLayout";
import { useState } from "react";
import { H3 } from "../../../../components/Typography/Typography";
import { EditButton } from "../../../../components/Buttons/Buttons";
import {
  InfoBlockSmall,
  InfoBlockRow,
} from "../../../../components/InfoBlocks/InfoBlocks";
import {
  AddressBlock,
  ContactBlock,
} from "../../../../components/LocationsList/LocationsList";
import { TextMdSemibold } from "../../../../components/Typography/TypographyV2";
import { LocationIcon, UserIcon } from "../../../../components/Icons/Icons";
import { EditGeneralInfo } from "../../OrganizationPage/CompanyInfo/Edit/EditGeneralInfo";
import { EditAddressForm } from "../../OrganizationPage/CompanyInfo/Edit/EditAddressForm";
import { EditLocations } from "../../OrganizationPage/CompanyInfo/Edit/EditLocations";
import styled from "styled-components/macro";
import type { Tenant, DataMutate } from "../../../../types/types";
import { formateInternationalPhoneNumber } from "../../../../util/phone";
import { useTranslation } from "react-i18next";
import { EditHQAddressForm } from "../../OrganizationPage/CompanyInfo/Edit/EditHQAddressForm";
import { SlideOut } from "../../../../components/SlideOut/SlideOut";
import { useStoreState } from "../../../../util/util";
import { Row } from "../../../../components/Layout/Layout";
import { useAuthContext } from "../../../../components/Auth";
import { EmailBlock } from "../shared";

const MultiLineBlock = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.regular};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  display: inline-block;
`;

const InfoBlockInnerRow = styled.div`
  margin-bottom: 36px;
  &:last-child {
    margin-bottom: ${({ theme }) => theme.resolve("spacing_none")};
  }
`;

function PrimaryContact({ tenant }: { tenant: Tenant }) {
  return (
    <MultiLineBlock>
      <div>
        {tenant.primary_contact && (
          <TextMdSemibold>
            {`${tenant.primary_contact?.first_name} ${tenant.primary_contact?.last_name}`}
          </TextMdSemibold>
        )}
      </div>
      <EmailBlock email={tenant.primary_contact?.email_address} />
      <div>
        <a href={`tel:${tenant.primary_contact.phone_number}`}>
          {formateInternationalPhoneNumber(tenant.primary_contact.phone_number)}
        </a>
      </div>
    </MultiLineBlock>
  );
}

// function ServicedByBlock() {
//   const Tabs = ["Self", "Auto Routing"];
//   const [activeTab, setActiveTab] = useState("Quote Requests");

//   const handleTabChange = (e: BaseSyntheticEvent) => {
//     setActiveTab(e.currentTarget.dataset.name);
//   };
//   return (
//     <>
//       <ToggleGroup
//         names={Tabs}
//         activeButton={activeTab}
//         clickHandler={handleTabChange}
//       />
//     </>
//   );
// }

export const CompanyInfo = ({
  buyerTenant,
  mutateBuyerTenant,
}: {
  buyerTenant: Tenant;
  mutateBuyerTenant: DataMutate<Tenant>;
}) => {
  const [editGeneralSection, setEditGeneralSection] = useState<boolean>(false);
  const [editBilling, setEditBilling] = useState(false);
  const [editHQAddress, setEditHQAddress] = useState(false);
  const { t } = useTranslation();
  const {
    storefront_metadata: { edition },
  } = useStoreState();
  const { roleIsSomeKindOfBuyer } = useAuthContext();

  const locationsAddresses = buyerTenant.addresses.filter(
    (address) => address.type === "Warehouse"
  );

  const billingAddress = buyerTenant.addresses.find(
    (address) => address.type === "Sold To Address"
  );

  const hqAddress = buyerTenant.addresses.find(
    (address) => address.type === "Headquarters Address"
  );

  const closeEditGeneralInfo = () => {
    mutateBuyerTenant();
    setEditGeneralSection(false);
  };

  return (
    <>
      <SettingsCard>
        <Row>
          <H3>{t("General")}</H3>
          <EditButton
            testid={"edit-general-company-info"}
            onClick={() => setEditGeneralSection(true)}
            title="Edit General Company Info"
          />
        </Row>
        <InfoBlockRow
          columns={
            !roleIsSomeKindOfBuyer &&
            buyerTenant.crm_id &&
            buyerTenant.crm_id !== ""
              ? 4
              : 3
          }
        >
          <div>
            <InfoBlockInnerRow>
              <InfoBlockSmall
                header={t("Company Name")}
                content={buyerTenant.name ?? "--"}
                icon={<LocationIcon width={16} />}
              />
            </InfoBlockInnerRow>{" "}
          </div>
          <div>
            <InfoBlockInnerRow>
              <InfoBlockSmall
                header={t("Doing Business As")}
                content={buyerTenant.doing_business_as ?? "--"}
              />
            </InfoBlockInnerRow>
          </div>
          {!roleIsSomeKindOfBuyer &&
            buyerTenant.crm_id &&
            buyerTenant.crm_id !== "" && (
              <div>
                <InfoBlockInnerRow>
                  <InfoBlockSmall
                    header={t("CRM ID")}
                    content={buyerTenant.crm_id ?? "--"}
                  />
                </InfoBlockInnerRow>
              </div>
            )}
          <div>
            <InfoBlockSmall
              header={t("Primary Contact")}
              content={<PrimaryContact tenant={buyerTenant} />}
              icon={<UserIcon width={16} />}
            />
          </div>
        </InfoBlockRow>
      </SettingsCard>
      <SlideOut
        show={editGeneralSection}
        closeFlyout={() => setEditGeneralSection(false)}
        title={t("General")}
      >
        <EditGeneralInfo
          tenant={buyerTenant}
          is_buyer_activated={true}
          closeEditGeneralSection={() => closeEditGeneralInfo()}
          onCancel={() => setEditGeneralSection(false)}
        />
      </SlideOut>
      <SettingsCard>
        <Row>
          <H3>{t("Headquarter")}</H3>
          {hqAddress && (
            <EditButton
              testid={"edit-headquarter-address"}
              onClick={() => setEditHQAddress(true)}
              title={t("Edit HeadQuarter")}
            />
          )}
        </Row>
        <InfoBlockRow columns={hqAddress ? 2 : 1}>
          <InfoBlockSmall
            header={t("Address")}
            content={<AddressBlock address={hqAddress} />}
            icon={<LocationIcon width={16} />}
          />
          {hqAddress && (
            <InfoBlockSmall
              header={t("Point of Contact")}
              content={<ContactBlock address={hqAddress} />}
            />
          )}
        </InfoBlockRow>
      </SettingsCard>
      {hqAddress && (
        <SlideOut
          show={editHQAddress}
          closeFlyout={() => setEditHQAddress(false)}
          title={t("Headquarter")}
        >
          <EditHQAddressForm
            address={hqAddress}
            setEdit={setEditHQAddress}
            tenantId={buyerTenant.id}
            mutateTenant={mutateBuyerTenant}
            onCancel={() => setEditHQAddress(false)}
          />
        </SlideOut>
      )}

      {edition !== "pim" && (
        <>
          <SettingsCard>
            <Row>
              <H3>{t("Billing")}</H3>
              {billingAddress && (
                <EditButton
                  testid={"tenant-edit-billing-address"}
                  onClick={() => setEditBilling(true)}
                  title="Edit Billing"
                />
              )}
            </Row>
            <InfoBlockRow columns={billingAddress ? 2 : 1}>
              <InfoBlockSmall
                header={t("Address")}
                content={<AddressBlock address={billingAddress} />}
                icon={<LocationIcon width={16} />}
              />
              {billingAddress && (
                <InfoBlockSmall
                  header={t("Point of Contact")}
                  content={<ContactBlock address={billingAddress} />}
                />
              )}
            </InfoBlockRow>
          </SettingsCard>
          {billingAddress && (
            <SlideOut
              show={editBilling}
              closeFlyout={() => setEditBilling(false)}
              title={t("Billing")}
            >
              <EditAddressForm
                address={billingAddress}
                setEdit={setEditBilling}
                tenantId={buyerTenant.id}
                mutateTenant={mutateBuyerTenant}
                hqAddress={hqAddress}
                onCancel={() => setEditBilling(false)}
              />
            </SlideOut>
          )}
          <SettingsCard>
            <EditLocations
              addresses={locationsAddresses}
              tenantId={buyerTenant.id}
              mutateTenant={mutateBuyerTenant}
              hqAddress={hqAddress}
            />
          </SettingsCard>
        </>
      )}
    </>
  );
};
